/**
 * @type Function
 * @description This is a helper function that is used to get the query params from the url. To read more about the Proxy class, check out this link: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy
 * @returns An object with the query params as properties. E.g. getQueryParams().id
 * @example const params = getQueryParams(); if (params.id) { // Do something }
 */
export function getQueryParams() {
  // This uses a Proxy object to get the query params as properties.
  // To read more about the Proxy class, check out this link: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop as string),
  })

  return params
}

/**
 * @type variable
 * @description Used to check if the current environment is a production environment.
 */
export const isProduction = process.env.NODE_ENV === 'production'

/**
 * @type interface
 * @description Here we are extending the Window interface with the grecaptcha property.
 */
declare let grecaptcha: any

/**
 * @type Function
 * @description This is a companion function that is used in "handleRecaptcha", can also be used as a standalone.
 * @returns A boolean value that indicates if the recaptcha is valid or not.
 * @example if (validateRecaptcha()) { // Do something }
 */
export function validateRecaptcha(): boolean {
  const v = grecaptcha.getResponse()

  if (v.length === 0) {
    return false
  }

  return true
}

/**
 * @type Function
 * @param  {Event} event
 * @param  {HTMLElement} element
 * @description This is the function that is used to validate the recaptcha and handles the error message as well.
 * @return void. It will prevent the default event if the recaptcha is not valid.
 * @example handleRecaptcha(event, element)
 */

export function handleRecaptcha(event: Event, element: HTMLElement, button: HTMLButtonElement = null): void {
  if (element) {
    if (!element.classList.contains('hide')) {
      element.classList.add('hide')
    }

    if (!validateRecaptcha()) {
      event.preventDefault()
      element.classList.remove('hide')

      if (button) {
        buttonLoader.reset(button)
      }
    }
  }
}

/**
 * @type Function
 * @param {NodeListOf<HTMLElement>} items
 * @param {String} className [open] - The class name that will be removed, can be overwritten.
 * @description This is a function that is used to remove "open" class from all elements.
 */
export async function removeActive(items: NodeListOf<HTMLElement>, className = 'open'): Promise<void> {
  items.forEach((item) => {
    item.classList.remove(className)
  })
}

export const ReduceArray = (arr, size: number) => {
  return arr.reduce((acc, val, i) => {
    const idx = Math.floor(i / size)
    // @ts-ignore
    const page = acc[idx] || (acc[idx] = [])
    page.push(val)

    return acc
  }, [])
}

import { SSConsts } from 'components/sailing-schedule/SSConsts'
import buttonLoader from './button-loader'
// import slideUp from './slide/slide-up'

/**
 * @type Function
 * @param {NodeListOf<HTMLElement>} items
 * @param {String} className [open] - The class name that will be removed, can be overwritten.
 * @description This is a function that is used to remove "open" class from all elements.
 */
export const toBoolean = (value: string) => {
  if (value !== '') {
    if (value === '1' || value === 'true' || value === 'True') {
      return true
    }

    if (value === '0' || value === 'false' || value === 'False') {
      return false
    }
  }

  return '[Not a boolean value]'
}

export const translateLineName = (value: string): string => {
  switch (value) {
    case 'Blue Line':
      return SSConsts.translation.blue
    case 'Red Line':
      return SSConsts.translation.red
    case 'Green Line':
      return SSConsts.translation.green
    case 'Yellow Line':
      return SSConsts.translation.yellow
    case 'Purple Line':
      return SSConsts.translation.purple
    case 'Orange Line':
      return SSConsts.translation.orange
    default:
      return ''
  }
}

export const scrollToTop = () => window.scroll({ top: 0, left: 0, behavior: 'smooth' })
export const scrollToBottom = () => window.scroll({ top: document.body.scrollHeight, left: 0, behavior: 'smooth' })

export const GetUrlParameter = (key: string) => {
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const getKey = urlParams.get(key)

  return getKey !== undefined && getKey !== null && getKey !== '' ? getKey : undefined
}
